import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Card, CardContent, Grid, Typography, Avatar, Tooltip, LinearProgress } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

import Skeleton from 'react-loading-skeleton';
import { labels, icons, colors } from 'views/Reports/constants';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    minHeight: 210,
    display: 'flex',
    flexDirection: 'column'
  },
  clickableCard: {
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[6],
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderColor: theme.palette.primary.main
    }
  },
  cardContent: {
    height: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  topSection: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between'
  },
  metricSection: {
    minWidth: '100px',
    flex: 1,
    paddingRight: theme.spacing(2)
  },
  title: {
    fontWeight: 700
  },
  avatar: {
    backgroundColor: theme.palette.primary,
    height: 56,
    width: 56
  },
  imageAvatar: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1)
  },
  icon: {
    height: 32,
    width: 32
  },
  imageIcon: {
    height: '100%',
    width: '100%',
    objectFit: 'contain',
    display: 'block'
  },
  bottomSection: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1.5)
  },
  subStatHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.75)
  },
  subStatText: {
    color: theme.palette.text.secondary,
    fontWeight: 700,
    lineHeight: 1
  },
  subStatInfoIcon: {
    fontSize: 16,
    marginLeft: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    verticalAlign: 'middle'
  },
  subStatProgress: {
    height: 8,
    borderRadius: 999,
    backgroundColor: theme.palette.action.hover,
    '& .MuiLinearProgress-barColorPrimary': {
      borderRadius: 999
    }
  },
  actionHint: {
    marginTop: 'auto',
    paddingTop: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: 12,
    gap: theme.spacing(0.5)
  },
  actionHintIcon: {
    fontSize: 16
  }
}));

const StatBox = props => {
  const {
    className,
    currentStat,
    previousStat,
    period,
    statType,
    isReportLoading = true,
    subStatText,
    subStatTooltip,
    subStatValue,
    onClick,
    ...rest
  } = props;

  const classes = useStyles();

  const label = labels[statType].toUpperCase();
  const Icon = icons[statType];
  const isClickable = typeof onClick === 'function';
  const isImageIcon = typeof Icon === 'string';

  const handleKeyDown = event => {
    if (!isClickable) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(event);
    }
  };

  return (
    <Card
      {...rest}
      className={clsx(classes.root, isClickable && classes.clickableCard, className)}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      style={isClickable ? { cursor: 'pointer' } : undefined}>
      <CardContent className={classes.cardContent}>
        <div className={classes.topSection}>
          <div className={classes.metricSection}>
            <Typography
              className={classes.title}
              color="textSecondary"
              gutterBottom
              variant="body2">
              {label}
            </Typography>

            <Typography variant="h3">
              {!isReportLoading ? (
                !isNaN(currentStat) ? new Intl.NumberFormat().format(currentStat) : (currentStat ? currentStat : 0)
              ) : (
                <Skeleton />
              )}
            </Typography>
          </div>
          <div>
            <Avatar
              style={{ backgroundColor: isImageIcon ? undefined : colors[statType] }}
              className={clsx(classes.avatar, isImageIcon && classes.imageAvatar)}>
              {isImageIcon ? (
                <img className={classes.imageIcon} src={Icon} alt={label} />
              ) : (
                <Icon className={classes.icon} />
              )}
            </Avatar>
          </div>
        </div>

        {!isReportLoading && subStatText && (
          <div className={classes.bottomSection}>
                <div className={classes.subStatHeader}>
                  <Typography variant="body2" className={classes.subStatText}>
                    {subStatText}
                  </Typography>
                  {subStatTooltip && (
                    <Tooltip title={subStatTooltip}>
                      <InfoIcon className={classes.subStatInfoIcon} />
                    </Tooltip>
                  )}
                </div>
                <LinearProgress
                  className={classes.subStatProgress}
                  value={typeof subStatValue === 'number' ? subStatValue : 0}
                  variant="determinate"
                />
          </div>
        )}

        {isClickable && !isReportLoading && (
          <div className={classes.actionHint}>
            <span>View details</span>
            <ArrowForwardIcon className={classes.actionHintIcon} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

StatBox.propTypes = {
  className: PropTypes.string
};

export default StatBox;
